import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import { UiaButtonIcon, UiaTextHeading, useUiContext } from "@rotorstudios/fe-ui";
import { gsap } from "@rotorstudios/fe-modules";

import Customise from "../../../Pages/Customise";
import Specifications from "../../../Pages/Specifications";
import Features from "../../../Pages/Features";
import Advantages from "../../../Pages/Advantages";
import Charging from "../../../Pages/Charging";
import Gallery from "../../../Pages/Gallery";
import Finance from "../../../Pages/Finance";
import {
  QA_MODAL_BUTTON_CLOSE,
  QA_MODAL_WRAPPER,
  QA_MODAL_HEADER,
  QA_MODAL_CONTENT,
} from "../../modules/Data/static";
import {
  MODAL_QTVR,
  MODAL_SPECIAL_OFFERS,
  MODAL_VEHICLE_SELECTOR,
  PAGE_ADVANTAGES,
  PAGE_CHARGING,
  PAGE_CUSTOMISE,
  PAGE_FEATURES,
  PAGE_FINANCE,
  PAGE_GALLERY,
  PAGE_SPECIFICATIONS,
} from "../../modules/fe-modules/Router/static";
import { UimSpecialOffers } from "../../uiComponents/molecules";
import UiMVehicleSelector from "../uiMVehicleSelector/UiMVehicleSelector";
import UiaIconClose from "../../uiComponents/atoms/uiaIconClose/UiaIconClose";
import useUiMModalOverlay from "./useUiMModalOverlay";
import UiMCMEQtvr from "../uiMCMEQtvr/UiMCMEQtvr";
import "./_UiMModalOverlay.scss";

const UiMModalOverlay = (): ReactElement => {
  const { breakpointActive } = useUiContext();
  const { modalState, modalId, modalData, handleClose } = useUiMModalOverlay();

  const ref = useRef<any>(null);
  const contentRef = useRef<any>(null);
  const [currentContent, setCurrentContent] = useState<string>("");

  const getClassName = () => classNames("UiMModalOverlay", breakpointActive);

  const handleComplete = (unmount: boolean) => {
    if (unmount) {
      setCurrentContent("");
    }
  };

  useEffect(() => {
    if (modalId) {
      setCurrentContent(modalId);
    }
  }, [modalId]);

  useEffect(() => {
    gsap.to(ref.current, 0.55, {
      y: modalId ? 0 : 75,
      display: modalId ? "initial" : "none",
      autoAlpha: modalId ? 1 : 0,
      onComplete: () => handleComplete(!modalId),
    });
    gsap.to(contentRef.current, 0.75, {
      autoAlpha: modalId ? 1 : 0,
      delay: modalId ? 0.25 : 0,
    });
  }, [modalId]);

  const content = useMemo(() => {
    switch (currentContent) {
      case PAGE_ADVANTAGES:
        return <Advantages />;
      case PAGE_CHARGING:
        return <Charging />;
      case PAGE_CUSTOMISE:
        return <Customise />;
      case PAGE_FEATURES:
        return <Features />;
      case PAGE_FINANCE:
        return <Finance />;
      case PAGE_GALLERY:
        return <Gallery />;
      case PAGE_SPECIFICATIONS:
        return <Specifications />;
      case MODAL_VEHICLE_SELECTOR:
        return <UiMVehicleSelector onClose={handleClose} />;
      case MODAL_SPECIAL_OFFERS:
        return <UimSpecialOffers {...modalData} onClose={handleClose} />;
      case MODAL_QTVR:
        return <UiMCMEQtvr />;
      default:
        return undefined;
    }
  }, [currentContent, handleClose, modalData]);

  const modalTitle = modalState.data?.title;
  const hasModalTitle = modalTitle ? "has-title" : "";
  const isCustomiseModal = currentContent === PAGE_CUSTOMISE ? "small-qtvr" : "";

  const overlayContent = (
    <div ref={ref} className={getClassName()}>
      <div className="UiMModalOverlay_Wrapper" data-qa={QA_MODAL_WRAPPER}>
        <div
          className={`UiMModalOverlay_Wrapper_Header ${hasModalTitle}`}
          data-qa={QA_MODAL_HEADER}
        >
          {modalTitle && <UiaTextHeading tText={modalTitle} H4 />}
          <UiaButtonIcon
            onClick={handleClose}
            icon={<UiaIconClose />}
            transparent
            qaId={QA_MODAL_BUTTON_CLOSE}
          />
        </div>
        <div
          ref={contentRef}
          className={`UiMModalOverlay_Wrapper_Content ${isCustomiseModal}`}
          data-qa={QA_MODAL_CONTENT}
        >
          {content}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    overlayContent,
    document.getElementById("EBRO_overlay-root") ?? document.createElement("div"),
  );
};

export default UiMModalOverlay;
